import React, { useEffect, useState, useContext } from 'react';
import { AmplifyAuthContext } from '../../App';
import { API } from 'aws-amplify';
import { makeStyles } from '@material-ui/core/styles';
import { library, IconDefinition } from '@fortawesome/fontawesome-svg-core';
import DealershipSelect from '../DealershipSelect';
import { getAPIName } from '../../config';
import handleApiErrors from '../../common/handleApiErrors';
import { useSnackbar } from 'notistack';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import useCustomSnack from '../helpers/handleSnackbars';
import DatePicker, { DateObject } from "react-multi-date-picker";
import moment from 'moment';
import {
  Button, CircularProgress, Divider, Grid, Typography,  Tooltip,  Collapse,Box, Tab, Tabs, IconButton
} from '@material-ui/core';
import { faFileExcel, faFileInvoice, faFilePdf, faFileDownload } from '@fortawesome/free-solid-svg-icons';
import ReactTable from "react-table";
import "react-table/react-table.css";
import { Bar } from "react-chartjs-2";
import "chartjs-plugin-datalabels";
import * as XLSX from "xlsx";

library.add(faFileExcel as IconDefinition, faFileInvoice as IconDefinition, faFilePdf );

const useStyles = makeStyles((theme) => ({
  today: {
    '& .rmdp-day.rmdp-today span': {
      backgroundColor: 'white',
      color: 'black',
      boxShadow: 'none'
    },
    '& .rmdp-day.rmdp-selected span:not(.highlight)': {
      backgroundColor: '#0074d9',
      boxShadow: '0 0 3px #8798ad',
      color: '#fff'
    }
  },
  iconSpacing: { marginLeft: theme.spacing(2), marginBottom: theme.spacing(0.5) },
  table: {
    border: '1px solid #ccc',
    boxShadow: 'rgba(50, 50, 93, 0.25) 0px 6px 12px -2px, rgba(0, 0, 0, 0.3) 0px 3px 7px -3px',
    borderCollapse: 'collapse',
    fontSize: '1.2em',
    width: '90%',
    marginTop: 20,
  },
  cell: { border: '1px solid #ccc', padding: '12px', textAlign: 'left' },
  valueCell: { border: '1px solid #ccc', padding: '12px', textAlign: 'center' },
  totalValueCell: { border: '1px solid #ccc', padding: '12px', color: 'black', textAlign: 'center' },
  loaderContainer: {
    display: "flex", justifyContent: "center", alignItems: "center",
    position: "absolute", top: "50%", left: "50%",
    transform: "translate(-50%, -50%)", zIndex: 5,
  },
  grow: { flexGrow: 1 },
  subMenu: {
    backgroundColor: '#484a54',
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(1),
  },
  downloadContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    height: 36,
    borderRadius: 6,
    background: '#f8f9fa',
    border: '1px solid #e9ecef',
    overflow: 'hidden',
  },
  downloadSection: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    '&:hover $downloadIcon': {
      transform: 'scale(1.1)',
    },
  },
  downloadIcon: {
    fontSize: 18,
    transition: 'all 0.2s ease',
  },
  separator: {
    width: 1,
    backgroundColor: '#ccc',
    height: '60%',
  },
  disabled: {
    opacity: 0.5,
    cursor: 'not-allowed',
    pointerEvents: 'none',
  },
  tableHeaders:{
    display: "inline-block",
    minWidth: 80,
    maxWidth: "100%",  
    textOverflow: "ellipsis",
    whiteSpace: "normal",
    wordBreak: "break-word",
  },
  tabFlex: {
  justifyContent: "flex-start",
  },
  customTooltip: {
    fontSize: "0.9rem !important",
    backgroundColor: "rgba(45,45,45,0.85) !important",
    color: "#fff !important",
    padding: "8px 10px !important",
    boxShadow: "0 0 6px rgba(0,0,0,0.3) !important",
    textAlign: "center",
  },
  customArrow: {
    color: "#2d2d2d !important",
  },
  monthPickerWrapper: {
  '& .rmdp-day.rmdp-disabled span': {
    opacity: 0.4,
    pointerEvents: 'none',
  },
  '& .rmdp-header .rmdp-title': {
    color: 'transparent',       // hide text       
    fontSize: 0,                // collapse space             
  },
  },
}));

interface FinancialSummaryItem {
  id: number;
  tolls_sum: string;
  invoices_sum: string;
  invoices_sum_via_paymentlinks: string;
  pgw_fees: string;
  pgw_fees_from_paymentlinks: string;
  service_fees: string | null;
  statement_total: string;
  active_vehicles_count: number;
  active_vin_count:number;
  lost_dispute_total: number;
  dispute_count:number;
  dispute_fee_total:number;
  tolls_amount_sum:number;
  month:number;
  year: number;
}

const formatUSD = (value: number | string): string => {
  if(value =="N/A"){
    return "N/A"
  }
  const numValue = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(numValue)) return '$ 0.00';
  
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(numValue).replace('$', '$ ');;
};

const FinancialReport = () => {
  const classes = useStyles();
  const handleCustomSnack = useCustomSnack();
  const [financialSummary, setFinancialSummary] = useState<FinancialSummaryItem[] | null>(null);
  const { enqueueSnackbar } = useSnackbar();
  const [searchType, setSearchType] = useState<string | unknown>('dealership');
  const [dealershipId, setDealershipId] = useState<number[] | undefined>([]);
  const [dealershipGroupId, setDealershipGroupId] = useState<number[] | undefined>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [selectedRange, setSelectedRange] = useState<DateObject[] | null>(null);
  const [startPeriod, setStartPeriod] = useState<string | null>(null);
  const [endPeriod, setEndPeriod] = useState<string | null>(null);
  const [csvIsLoading, setCsvIsLoading] = useState<boolean>(false);
  const [lastFetchWasSingleEntity, setLastFetchWasSingleEntity] = useState<boolean>(false);

  const now = new DateObject();
  const lastMonth = now.subtract(1, "month");
  const maxDate = new DateObject({
    year: lastMonth.year,
    month: lastMonth.month.number,
    day: new Date(lastMonth.year, lastMonth.month.number, 0).getDate()
  });

  const currentAuthenticatedUser: any = useContext(AmplifyAuthContext);
  const userRole: string = currentAuthenticatedUser?.attributes?.given_name ?? 'Individual';

  const handleRangeChange = (range: DateObject[] | null) => {
    if (range?.length === 2) {
      setSelectedRange(range);
      setStartPeriod(range[0].format("YYYY-MM"));
      setEndPeriod(range[1].format("YYYY-MM"));
    }
  };

  const handleSearch = async (): Promise<void> => {
    if (searchType === '') {
      handleCustomSnack('select dealership or dealership-group', 'warning');
      return;
    }
    if ((searchType === 'dealership' && dealershipId && dealershipId.length == 0) ||
        (searchType === 'dealershipGroup' && dealershipGroupId && dealershipGroupId.length == 0 )) {
      const errorMessage = searchType === 'dealership'
        ? "Please select a dealership"
        : "Please select a dealership group";
      handleCustomSnack(errorMessage, 'warning');
      return;
    }
    if (!startPeriod || !endPeriod) {
      handleCustomSnack("Please select month range", "warning");
      return;
    }

    setIsLoading(true);
    let queries: Array<{ name: string; value: string }> = [];

    if (dealershipId && dealershipId.length > 0 && searchType === 'dealership') {
        dealershipId.forEach(id => {
          queries.push({ name: 'dealershipid', value: String(id)});
        });
      }
      if (dealershipGroupId && dealershipGroupId.length > 0 && searchType === 'dealershipGroup') {
          dealershipGroupId.forEach(id => {
          queries.push({ name: 'dealershipgroupid', value: String(id)});
        });
      }
    queries.push({ name: 'start-period', value: startPeriod });
    queries.push({ name: 'end-period', value: endPeriod });

    let path: string = '/api/v1/financialsummary';
    if (queries.length > 0) {
      const paramStrings: string[] = queries.map(q =>
        `${encodeURIComponent(q.name)}=${encodeURIComponent(q.value)}`
      );
      path = `${path}?${paramStrings.join("&")}`;
    }
    try {
      const response = await API.get(getAPIName(), path, {});
      if (response && response.error) {
        handleCustomSnack(response?.error, 'warning');
        setFinancialSummary(null);
      } else {
        setFinancialSummary(response.body);
        setLastFetchWasSingleEntity(
          (searchType === 'dealership' && dealershipId?.length === 1) ||
          (searchType === 'dealershipGroup' && dealershipGroupId?.length === 1)
        );
      }
    } catch (e) {
      setFinancialSummary(null);
      handleApiErrors(e, enqueueSnackbar);
    } finally {
      setIsLoading(false);
    }
  };
// Fetch Presigned URL from API's
  const downloadFile = async (filetype: string, apiInit: any = {}, id?: number) => {
  try {
    const fileId = id ?? (financialSummary !== null ? financialSummary[0].id : null);
    if (!fileId) return;
    const path = `/api/v1/financialsummary/download/${fileId}?filetype=${filetype}`;
    const response = await API.get(getAPIName(), path, apiInit);
    if (response && response.presignedUrl) {
      window.open(response.presignedUrl, '_blank');
      handleCustomSnack(`File Downloaded Successfully`, 'success')
    }
    else if (response && response.error) {
      handleCustomSnack(response?.error, 'warning')
    }
  } catch (error) {
    handleApiErrors(error, enqueueSnackbar);
  }
};

const downloadExcel = () => {
  if (!financialSummary?.length) return;

  setCsvIsLoading(true);

  const excelData = [...data, totals].map(row => ({
    'Month': row.monthLabel,
    ...(userRole === "Admin" && { 'Tolls Paid (Internal)': +row.tolls_amount_sum }),
    'Tolls Paid': +row.tolls_sum,
    'Invoices (CC On File)': +row.invoices_sum,
    'Invoices (Payment Links)': +row.invoices_sum_via_paymentlinks,
    'PGW Fees (CC On File)': +row.pgw_fees,
    'PGW Fees (Payment Links)': +row.pgw_fees_from_paymentlinks,
    'Lost Disputes': +row.lost_dispute_total,
    'Dispute Fees': +row.dispute_fee_total,
    'Statement Total': +row.statement_total,
    'Vehicle Count': +(String(row.active_vehicles_count).replace('*','') || 0),
    'Vin Count': +(String(row.active_vin_count).replace('*','') || 0),
    'Dispute Count': +(String(row.dispute_count).replace('*','') || 0),
  }));

  const wb = XLSX.utils.book_new();
  const ws = XLSX.utils.json_to_sheet(excelData);

  // Dynamically find numeric columns for currency formatting
  const headerKeys = Object.keys(excelData[0]);
  headerKeys.forEach((key, colIndex) => {
    const isCurrency = ['Tolls Paid', 'Tolls Paid (Internal)', 'Invoices (CC On File)', 'Invoices (Payment Links)', 'PGW Fees (CC On File)', 'PGW Fees (Payment Links)', 'Lost Disputes', 'Dispute Fees', 'Statement Total'].includes(key);
    if (!isCurrency) return;

    for (let rowIndex = 2; rowIndex <= excelData.length + 1; rowIndex++) {
      const cellAddress = XLSX.utils.encode_cell({ c: colIndex, r: rowIndex - 1 });
      const cell = ws[cellAddress];
      if (cell && typeof cell.v === 'number') cell.z = '$#,##0.00';
    }
  });

  XLSX.utils.book_append_sheet(wb, ws, 'Financial Summary');
  XLSX.writeFile(wb, `Financial_Summary_${moment().format('YYYY-MM-DD_HH-mm-ss')}.xlsx`);
  setCsvIsLoading(false);
};

  const columns = [
    { Header: "Months", accessor: "monthLabel",width:80, sortable: false, },
    ...(userRole === "Admin"
      ? [{ Header: () => (
        <Tooltip title="Tolls Paid (Internal)" classes={{ tooltip: classes.customTooltip, arrow: classes.customArrow }} arrow>
          <span className={classes.tableHeaders}>
            Tolls Paid <br /> (Internal)
          </span>
        </Tooltip>
        ), accessor: "tolls_amount_sum",
        sortable: false,
      Cell: (row: any) => formatUSD(row.value) }]
      : []),
    { Header: () => (
        <Tooltip title="Total amount of Tolls paid to the toll authorities" classes={{ tooltip: classes.customTooltip, arrow: classes.customArrow }} arrow>
          <span className={classes.tableHeaders}>
            Tolls Paid
          </span>
        </Tooltip>
        ), 
        accessor: "tolls_sum",
        sortable: false,
        Cell: (row: any) => formatUSD(row.value) },
    { Header: () => (
        <Tooltip title="Total amount of customer toll invoices collected using the card on file" classes={{ tooltip: classes.customTooltip, arrow: classes.customArrow }} arrow>
          <span className={classes.tableHeaders}>
            Invoices <br />(CC On File)
          </span>
        </Tooltip>
        ), 
        accessor: "invoices_sum",
        sortable: false,
      Cell: (row: any) => formatUSD(row.value) },
    { Header: () => (
        <Tooltip title="Total amount of customer toll invoices collected using the links sent to customer via email" classes={{ tooltip: classes.customTooltip, arrow: classes.customArrow }} arrow>
          <span className={classes.tableHeaders}>
            Invoices<br /> (Payment Links)
          </span>
        </Tooltip>
        ), accessor: "invoices_sum_via_paymentlinks", sortable: false, Cell: (row: any) => formatUSD(row.value) },
    { 
      Header: () => (
        <Tooltip title="Total amount of fees paid to the payment gateway for the toll invoices collected using the card on file" classes={{ tooltip: classes.customTooltip, arrow: classes.customArrow }} arrow>
          <span className={classes.tableHeaders}>
            PGW Fees<br /> (CC On File)
          </span>
        </Tooltip>
        ),
         accessor: "pgw_fees",
         sortable: false,
         Cell: (row: any) => formatUSD(row.value) 
    },
    {
      Header: () => (
        <Tooltip title=" Total amount of fees paid to the payment gateway for the toll invoices collected the links sent to customer via email" classes={{ tooltip: classes.customTooltip, arrow: classes.customArrow }} arrow>
          <span className={classes.tableHeaders}>
            PGW Fees <br />(Payment Links)
          </span>
        </Tooltip>
      ),
      accessor: "pgw_fees_from_paymentlinks",
      sortable: false,
      Cell: (row: any) => formatUSD(row.value)
    },
    { Header: () => (
        <Tooltip title="Total amount of toll invoice disputes that were lost." classes={{ tooltip: classes.customTooltip, arrow: classes.customArrow }} arrow>
          <span className={classes.tableHeaders}>
            Lost Disputes
          </span>
        </Tooltip>
      ), accessor: "lost_dispute_total",sortable: false,Cell: (row: any) => formatUSD(row.value) },
    { Header: () => (
        <Tooltip title="Total amount of fees paid for each disputed transaction" classes={{ tooltip: classes.customTooltip, arrow: classes.customArrow }} arrow>
          <span className={classes.tableHeaders}>
            Dispute Fees
          </span>
        </Tooltip>
      ), accessor: "dispute_fee_total",
      sortable: false,
    Cell: (row: any) => formatUSD(row.value) },
    { Header: () => (
        <Tooltip title="Statement Total" classes={{ tooltip: classes.customTooltip, arrow: classes.customArrow }} arrow>
          <span className={classes.tableHeaders}>
            Statement <br />Total
          </span>
        </Tooltip>
      ), 
      accessor: "statement_total" ,sortable: false,Cell: (row: any) => formatUSD(row.value)
    },
    {
      Header: () => (
        <Tooltip title="Number of vehicles with tolls" classes={{ tooltip: classes.customTooltip, arrow: classes.customArrow }} arrow>
          <span className={classes.tableHeaders}>
            Vehicle Count
          </span>
        </Tooltip>
      ),
      width:80,
      headerStyle: { borderLeft: "2px solid #ccc" },
      accessor: "active_vehicles_count",sortable: false,
      getProps: () => ({ style: { borderLeft: "2px solid #ccc" } })
    },
    {
      Header: () => (
        <Tooltip title="Number of vehicles with tolls" classes={{ tooltip: classes.customTooltip, arrow: classes.customArrow }} arrow>
          <span className={classes.tableHeaders}>
            VIN <br/>Count
          </span>
        </Tooltip>
      ),
      width:80,
      accessor: "active_vin_count",sortable: false,
    },
    { Header: () => (
        <Tooltip title="Count of toll invoice disputes" classes={{ tooltip: classes.customTooltip, arrow: classes.customArrow }} arrow>
          <span className={classes.tableHeaders}>
            Dispute Count
          </span>
        </Tooltip>
      ), 
      width:80,
      sortable: false,
      accessor: "dispute_count" 
    },
    {
    Header: () => (
      <Tooltip title="Status" classes={{ tooltip: classes.customTooltip, arrow: classes.customArrow }} arrow>
        <span className={classes.tableHeaders}>
          Status
        </span>
      </Tooltip>
    ), 
    accessor: "invoice_status",
    sortable: false,
    Cell: (row: any) => row.value || "-"
    },
    ...((lastFetchWasSingleEntity) ? [{
        Header: () => (
          <Tooltip title="Downloads" classes={{ tooltip: classes.customTooltip, arrow: classes.customArrow }} arrow>
            <span className={classes.tableHeaders}>Downloads</span>
          </Tooltip>
        ),
        width: 100,
        accessor: "download",
        Cell: (row: any) => {
          const { statement_bucketpath, bill_bucketpath, id, monthLabel } = row.original;
          if (monthLabel === "TOTAL" || monthLabel === "Total") return null;
          const isStatementDisabled = !statement_bucketpath;
          const isBillDisabled = !bill_bucketpath;
          return (
            <div className={classes.downloadContainer}>
              {/* Excel */}
              <Tooltip title={isStatementDisabled ? "No Report available" : "Download Report"} classes={{ tooltip: classes.customTooltip, arrow: classes.customArrow }} arrow>
                <div
                  className={`${classes.downloadSection} ${isStatementDisabled ? classes.disabled : ""}`}
                  onClick={() => !isStatementDisabled && downloadFile("statement", {}, id)}
                >
                  <FontAwesomeIcon
                    icon={faFileExcel}
                    className={classes.downloadIcon}
                    style={{ color: isStatementDisabled ? "#aaa" : "#43A047" }}
                  />
                </div>
              </Tooltip>

              <div className={classes.separator} />

              {/* PDF */}
              <Tooltip title={isBillDisabled ? "No Bill available" : "Download Bill"} classes={{ tooltip: classes.customTooltip, arrow: classes.customArrow }} arrow>
                <div
                  className={`${classes.downloadSection} ${isBillDisabled ? classes.disabled : ""}`}
                  onClick={() => !isBillDisabled && downloadFile("bill", {}, id)}
                >
                  <FontAwesomeIcon
                    icon={faFilePdf}
                    className={classes.downloadIcon}
                    style={{ color: isBillDisabled ? "#aaa" : "#D32F2F" }}
                  />
                </div>
              </Tooltip>
            </div>
          );
        },
      }] : [])
  ];

  const totals = { monthLabel: "Total" } as any;
  // Total for every column
  columns.forEach(col => {
    if (col.accessor && !["monthLabel", "invoice_status"].includes(col.accessor)) {
      totals[col.accessor] = financialSummary?.reduce(
        (sum, f) => sum + (Number(f[col.accessor as keyof FinancialSummaryItem]) || 0),
        0
      ).toFixed(2) || 0;
    }
  });
  const grouped = (financialSummary || []).reduce((acc, f) => {
    const key = `${f.year}-${String(f.month).padStart(2, "0")}`;
    if (!acc[key]) acc[key] = { ...f };
    else {
      columns.forEach(col => {
        const k = col.accessor as keyof FinancialSummaryItem;
        if (!["monthLabel", "year", "month"].includes(k)) {
          const val = Number(f[k]) || 0;
          (acc[key][k] as unknown as number) = (Number(acc[key][k]) || 0) + val;
        }
      });
    }
    return acc;
  }, {} as Record<string, FinancialSummaryItem>);

  let data = Object.values(grouped)
    .map(f => ({
      ...f,
      monthLabel: moment(`${f.year}-${String(f.month).padStart(2, "0")}`, "YYYY-MM").format("MMM YYYY")
    }))
    .sort((a, b) => (a.year - b.year) || (a.month - b.month));
    // Fill missing months if range is selected
  if (startPeriod && endPeriod && data.length) {
    const start = moment(startPeriod, "YYYY-MM");
    const end = moment(endPeriod, "YYYY-MM");
    const dataMap = Object.fromEntries(data.map(d => [`${d.year}-${d.month}`, d]));
    data = [];
    while (start.isSameOrBefore(end, 'month')) {
      const year = start.year(), month = start.month() + 1;
      const key = `${year}-${month}`;
      const existing = dataMap[key];
      data.push(existing || {
        year, month,
        monthLabel: start.format("MMM YYYY"),
        ...Object.fromEntries(columns
          .filter(c => c.accessor && c.accessor !== "monthLabel")
          .map(c => [c.accessor, "N/A"]))
      });
      start.add(1, 'month');
    }
  }
  // Average for Counts
  if (data && data.length > 0) {
    const count = data.length;
    totals.active_vehicles_count = `${Math.round(data.reduce((s, f) => s + f.active_vehicles_count, 0) / count)}*`;
    totals.active_vin_count = `${Math.round(data.reduce((s, f) => s + f.active_vin_count, 0) / count)}*`;
    totals.dispute_count = `${Math.round(data.reduce((s, f) => s + f.dispute_count, 0) / count)}*`;
  }
  // Chart Config:
  const labels = data?.map(i => `${new Date(i.year, i.month - 1).toLocaleString('en-US', { month: 'short' })} ${String(i.year).slice(-2)}`);

  const chartData = {
    labels,
    datasets: [
      // 1. Tolls bar
      {
        label: "Tolls Paid",
        type: "bar",
        backgroundColor: "rgba(76,175,80,0.75)",
        borderColor: "rgba(76,175,80,1)",
        borderWidth: 1.5,
        data: data?.map(i => parseFloat(i.tolls_sum)),
        yAxisID: "y-axis-amount",
        stack:'tollsAndPgwFees',
        order: 3,
      },
      // 2. Invoices bar - Stacked components
      {
        label: "Invoices (CC On File)",
        type: "bar",
        backgroundColor: "rgba(33,150,243,0.85)",
        borderColor: "rgba(33,150,243,1)",
        borderWidth: 1,
        data: data?.map(i => Math.abs(parseFloat(i.invoices_sum))),
        yAxisID: "y-axis-amount",
        stack: "charges",
        order: 3,
      },
      {
        label: "Invoices (Payment Links)",
        type: "bar",
        backgroundColor: "rgba(66,165,245,0.85)",
        borderColor: "rgba(66,165,245,1)",
        borderWidth: 1,
        data: data?.map(i => Math.abs(parseFloat(i.invoices_sum_via_paymentlinks))),
        yAxisID: "y-axis-amount",
        stack: "charges",
        order: 3,
      },
      // Payment Gateway Fees Bar [stacked with tolls]
      {
        label: "PGW Fees (CC On File)",
        type: "bar",
        backgroundColor: "rgba(100,195,80,0.75)",
        borderColor: "rgba(100,195,80,1)",
        borderWidth: 1,
        data: data?.map(i => Math.abs(parseFloat(i.pgw_fees))),
        yAxisID: "y-axis-amount",
        stack:'tollsAndPgwFees',
        order: 3,
      },
      {
        label: "PGW Fees (Payment Links)",
        type: "bar",
        backgroundColor: "rgba(130,215,80,0.75)",
        borderColor: "rgba(130,215,80,1)",
        borderWidth: 1,
        data: data?.map(i => Math.abs(parseFloat(i.pgw_fees_from_paymentlinks))),
        yAxisID: "y-axis-amount",
        stack:'tollsAndPgwFees',
        order: 3,
      },
      // 3. Statement Total line
      {
        label: "Statement Total",
        type: "line",
        borderWidth: 3,
        fill: false,
        data: data?.map(i => Math.abs(parseFloat(i.statement_total))),
        yAxisID: "y-axis-amount",
        order: 2,
        tension: 0.3,
        pointRadius: 4,
        pointHoverRadius: 6,
        borderColor: "rgba(51,51,51,1)",          
        backgroundColor: "rgba(51,51,51,0.1)",  
        pointBackgroundColor: "rgba(51,51,51,1)",
        pointBorderColor: "#fff",
        pointBorderWidth: 2,
        pointHoverBackgroundColor: "#fff",
        pointHoverBorderColor: "rgba(51,51,51,1)"
      },
      // 4. Active Vehicles Count lines
      {
        label: "Vehicle Count",
        type: "line",
        borderWidth: 2.5,
        fill: false,
        data: data?.map(i => i.active_vehicles_count),
        yAxisID: "y-axis-count",
        order: 1,
        tension: 0.3,
        pointRadius: 3,
        pointHoverRadius: 5,   
        borderColor: "rgba(156,39,176,1)",
        backgroundColor: "rgba(156,39,176,0.1)",  
        pointBackgroundColor: "rgba(156,39,176,1)",
        pointBorderColor: "#fff",
        pointBorderWidth: 2,
        borderDash: [5, 3]
      },
       // 4. Dispute Count lines
      {
        label: "Dispute Count",
        type: "line",
        borderColor: "rgba(244,67,54,1)",
        backgroundColor: "rgba(244,67,54,0.1)",
        borderWidth: 2.5,
        fill: false,
        data: data?.map(i => i.dispute_count),
        yAxisID: "y-axis-count",
        order: 1,
        tension: 0.3,
        pointRadius: 3,
        pointHoverRadius: 5,
        pointBackgroundColor: "rgba(244,67,54,1)",
        pointHoverBorderColor: "rgba(244,67,54,1)",
        pointBorderColor: "#fff",
        pointBorderWidth: 2,
        borderDash: [5, 3]
      }
    ]
  };
  const barCount = chartData.datasets.filter(d => d.type === 'bar').length || 1;
  const barThickness = Math.max(10, Math.min(50, Math.floor(2200 / (labels.length * barCount))));

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index',
      intersect: false,
    },
    tooltips: {
      mode: 'nearest',
      intersect: true,
      backgroundColor: 'rgba(0,0,0,0.8)',
      titleFontSize: 13,
      bodyFontSize: 12,
      bodySpacing: 6,
      xPadding: 12,
      yPadding: 12,
      callbacks: {
        label: function(tooltipItem:any, data:any) {
          const dataset = data.datasets[tooltipItem.datasetIndex];
          const value = tooltipItem.yLabel;
          // Format based on axis
          if (dataset.yAxisID === 'y-axis-count') {
            return dataset.label + ': ' + Math.round(value).toLocaleString();
          } else {
            return dataset.label + ': $' + value.toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2
            });
          }
        }
      }
    },
    legend: {
      display: true,
      position: 'bottom',
      labels: {
        usePointStyle: true,
        padding: 15,
        fontSize: 11,
        boxWidth: 8,
        generateLabels: (chart: any) => {
              const order = [
                "Tolls Paid",
                "Invoices (CC On File)",
                "Invoices (Payment Links)",
                "PGW Fees (CC On File)",
                "PGW Fees (Payment Links)",
                "Statement Total",
                "Vehicle Count",
                "Dispute Count"
              ];
              return order.map(label => {
                const dsIndex = chart.data.datasets.findIndex((d: any) => d.label === label);
                if (dsIndex < 0) return null;
                const ds = chart.data.datasets[dsIndex];
                return {
                  text: label,
                  fillStyle: ds.backgroundColor,
                  strokeStyle: ds.borderColor,
                  hidden: chart.getDatasetMeta(dsIndex).hidden,
                  datasetIndex: dsIndex
                };
              });
            }
      }
    },
    scales: {
      xAxes: [{
        gridLines: { display: false, drawBorder: true },
        ticks: { fontSize: 11, fontStyle: 'bold' },
        barThickness,
        barPercentage: 1,
        categoryPercentage: 0.8,
      }],
      yAxes: [
        {
          id: "y-axis-amount",
          position: "left",
          scaleLabel: {
            display: true,
            labelString: "Amount ($)",
            fontSize: 13,
            fontStyle: 'bold',
            fontColor: '#333'
          },
          ticks: {
            beginAtZero: true,
            fontSize: 11,
            callback: function(value:any) {
              return '$' + value.toLocaleString();
            }
          },
          gridLines: {
            color: 'rgba(0,0,0,0.08)',
            zeroLineColor: 'rgba(0,0,0,0.2)',
            zeroLineWidth: 2
          }
        },
        {
          id: "y-axis-count",
          position: "right",
          scaleLabel: {
            display: true,
            labelString: "Count (Vehicles / Disputes)",
            fontSize: 13,
            fontStyle: 'bold',
            fontColor: '#666'
          },
          ticks: {
            beginAtZero: true,
            fontSize: 11,
            callback: function(value:any) {
              return Math.round(value).toLocaleString();
            }
          },
          gridLines: {
            drawOnChartArea: false,
            color: 'rgba(0,0,0,0.08)'
          }
        }
      ]
    },
    plugins: {
      datalabels: {
        display: false
      }
    }
  };

  return (
    <div className='tollreports'>
      <Grid container>
        <Grid item xs={9}>
          <Typography variant="h4" align="left"> Financial Reports </Typography>
        </Grid>
        <Grid item xs={3}>
          <Button size="small" variant="contained" color="primary" onClick={downloadExcel} disabled={!financialSummary}>
            {!csvIsLoading ? 'Download Excel' :
              <CircularProgress size={24} />
            }
          </Button>
        </Grid>
      </Grid>
      <Divider />
      <div className={classes.grow}>
        <Grid container direction="row" justify="center" alignItems="center" className={classes.subMenu} >
          <Grid item xs={12} md={8}>
            <DealershipSelect
              dealershipId={dealershipId}
              setDealershipId={setDealershipId}
              dealershipGroupId={dealershipGroupId}
              setDealershipGroupId={setDealershipGroupId}
              searchType={searchType}
              setSearchType={setSearchType}
              enableAutocomplete={true}
              enableMultiSelect={true}
            />

          </Grid>
          <div className={classes.grow} />
          <Grid xs={12} md={4} className={classes.today}>
            <div className={classes.monthPickerWrapper}>
              <label style={{ color: 'white' }}>Select Month Range: </label>
              <DatePicker
                range
                numberOfMonths={2}
                format="YYYY-MM"
                onlyMonthPicker
                editable={false}
                value={selectedRange}
                maxDate={maxDate}
                currentDate={lastMonth}     
                onChange={handleRangeChange}
                style={{textAlign:'center', backgroundColor:'#6f7179', height:'35px', color:'white', border:'none', fontWeight:500}}
              />
            </div>
          </Grid>
        </Grid>
        <br />
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Button variant="contained" color="primary" onClick={handleSearch} > Search </Button>
          </Grid>
        </Grid>
        <br />
      </div>

      {isLoading ? (
        <div className={classes.loaderContainer}><CircularProgress /></div>
      ) : (
        financialSummary && (
          <>
          <ReactTable
            data={(financialSummary && financialSummary.length > 0) ? [...data, totals]:[{ monthLabel: "No Data", isNoData: true }]}
            columns={columns}
            style={{ borderRadius: 12, overflow: 'hidden',boxShadow: '0 4px 8px rgba(0,0,0,0.1)' }}
            defaultPageSize={data.length + 1}
            showPagination={false}
            className="-striped -highlight"
            getTheadThProps={() => ({
              style: {
                fontWeight: 600,
                fontSize: '14px',
                textAlign: 'center',
                color: 'white',
                backgroundColor: '#333',
                padding: '15px 0px',
                verticalAlign: 'middle',
                display:'flex',
                alignItems:'center',
                justifyContent:'center',
              },
            })}
            getTrProps={(_: any, rowInfo?: { original: any }) => ({
              style:
                rowInfo && rowInfo.original.monthLabel === "Total"
                  ? {
                      fontWeight: "bold",
                      background: "#333",
                      color: "#fff",
                      verticalAlign: "middle",
                    }
                  : {},
            })}
            getTdProps={(_: any, rowInfo?: { original: any }) => ({
              style: {
                textAlign: "center",
                display:'flex',
                verticalAlign: "middle",
                alignItems:'center',
                justifyContent:'center',
                padding: "12px",
                ...(rowInfo && rowInfo.original.monthLabel === "Total"
                  ? {
                      fontWeight: "bold",
                      background: "#333",
                      color: "#fff",
                      padding: "15px 0px",
                    }
                  : {}),
              },
            })}
          />

          <p style={{textAlign: 'right', marginRight: '20px', marginBottom: '15px', fontSize: '16px', fontWeight: 'bold', color: '#666'}}>
            *  Average
          </p>
          <Grid item xs={9}>
            <Typography variant="h5" align="left" style={{marginTop:'10px', marginLeft:'15px'}}> Chart:</Typography>
          </Grid>
          <div id="chartWrap" style={{ height:'500px', marginTop:'20px', position: 'relative',border: '1px solid #ccc', borderRadius: 8, padding: '30px 16px 10px 16px', boxShadow: '0 4px 8px rgba(0,0,0,0.1)' }}>
            <Bar data={chartData} options={options} />
          </div>
          </>
        )
      )}
    </div>
  );
};

export default FinancialReport;